import { BaseFeature } from '../../core/base-feature.js';

/**
 * BattleshipFeature - Frontend feature for Battleship game
 *
 * Controls the desktop overlay game from the frontend.
 * Receives full game state including both grids and all ship positions (intel advantage).
 * Can move ships after being hit, sabotage desktop shots, and fire with perfect accuracy.
 */
export class BattleshipFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'battleship',
            name: 'Battleship',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'fire', 'move_ship', 'sabotage', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Battleship] Initializing...');
        console.log('[Battleship] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'place_ships_response':
            case 'fire_response':
            case 'move_ship_response':
            case 'sabotage_response':
            case 'get_state_response':
            case 'end_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Battleship] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    startGame() {
        return this._sendRequest('start');
    }

    fire(x, y) {
        return this._sendRequest('fire', { x, y });
    }

    moveShip(shipId, newPosition) {
        return this._sendRequest('move_ship', { shipId, newPosition });
    }

    sabotage(action) {
        return this._sendRequest('sabotage', { action });
    }

    getState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // Event subscriptions for React
    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Battleship] Error in state callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[Battleship] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
